// v2.6 scan capture (TYR-232), consultant `/internal` only: the risky
// permission matrix under Permissions, and integration, UI build, connected
// app use and package flags under Apps. Rendered from the stored bundle; a
// null section says "Not captured in this scan", never a guess.
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { EmptyState } from '../ui/EmptyState';

type RiskyFlag =
  | 'modifyAllData'
  | 'viewAllData'
  | 'dataExport'
  | 'authorApex'
  | 'apiEnabled'
  | 'manageUsers'
  | 'manageSharing';

export type IntegrationSecurityResult = {
  integration: {
    namedCredentials: { name: string; host: string | null }[] | null;
    remoteSites: { name: string; host: string | null; active: boolean }[] | null;
    outboundMessages: { name: string; object: string | null }[] | null;
    platformEvents: string[] | null;
  };
  uiBuild: {
    lightningPagesByType: Record<string, number> | null;
    lwc: number | null;
    aura: number | null;
    visualforcePages: number | null;
    visualforceComponents: number | null;
    experienceSites: { name: string; status: string | null }[] | null;
  };
  riskyPermissions:
    | {
        kind: 'profile' | 'permission_set';
        name: string;
        namespacePrefix: string | null;
        flags: Record<RiskyFlag, boolean>;
        activeAssignments: number | null;
      }[]
    | null;
  connectedAppUse: { appName: string; lastUsedDate: string | null; userCount: number; useCount: number }[] | null;
  packageFlags:
    | {
        name: string;
        namespacePrefix: string | null;
        version: string | null;
        isManaged: boolean | null;
        isDeprecated: boolean | null;
        isSecurityReviewed: boolean | null;
        isPasswordProtected: boolean | null;
        releaseState: string | null;
        outOfDate: 'unknown';
      }[]
    | null;
};

const FLAG_COLS: { key: RiskyFlag; label: string }[] = [
  { key: 'modifyAllData', label: 'Modify All Data' },
  { key: 'viewAllData', label: 'View All Data' },
  { key: 'dataExport', label: 'Data Export' },
  { key: 'authorApex', label: 'Author Apex' },
  { key: 'apiEnabled', label: 'API Enabled' },
  { key: 'manageUsers', label: 'Manage Users' },
  { key: 'manageSharing', label: 'Manage Sharing' },
];

const NOT_CAPTURED = 'Not captured in this scan';
const TH = 'text-left px-3 py-2 font-medium';
const TD = 'px-3 py-2';

function Missing() {
  return <div className="text-sm text-neutral-500 italic">{NOT_CAPTURED}</div>;
}

function yn(v: boolean | null): string {
  return v == null ? 'Unknown' : v ? 'Yes' : 'No';
}

function num(v: number | null | undefined): string {
  return v == null ? NOT_CAPTURED : v.toLocaleString();
}

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  if (rows.length === 0) return <div className="text-sm text-neutral-500">None in this org.</div>;
  return (
    <div className="border border-border rounded-md overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-2">
          <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
            {head.map((h, i) => (
              <th key={h} className={i === 0 ? TH : `${TH} text-right`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
              {r.map((c, j) => (
                <td key={j} className={j === 0 ? `${TD} text-crux-light` : `${TD} text-right text-neutral-300 tabular-nums`}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreV26() {
  return (
    <EmptyState
      title="Integration, UI build and security detail was not captured for this scan."
      hint="This scan predates v2.6. A new scan captures it."
    />
  );
}

export function RiskyPermissionMatrix({ data }: { data: IntegrationSecurityResult | null | undefined }) {
  if (!data) return <PreV26 />;
  const rows = data.riskyPermissions;
  return (
    <CollapsibleSection title="Risky permissions" meta={rows ? `${rows.length} profiles and permission sets` : NOT_CAPTURED}>
      {rows ? (
        <Table
          head={['Profile or permission set', 'Type', ...FLAG_COLS.map((f) => f.label), 'Active assignments']}
          rows={rows.map((r) => [
            r.namespacePrefix ? `${r.name} (${r.namespacePrefix})` : r.name,
            r.kind === 'profile' ? 'Profile' : 'Permission set',
            ...FLAG_COLS.map((f) => (r.flags[f.key] ? 'Yes' : '')),
            r.activeAssignments == null ? 'Unknown' : r.activeAssignments,
          ])}
        />
      ) : (
        <Missing />
      )}
    </CollapsibleSection>
  );
}

export function AppsScanDetail({
  data,
  apiLimit,
}: {
  data: IntegrationSecurityResult | null | undefined;
  apiLimit: { Max: number; Remaining: number } | undefined;
}) {
  if (!data) return <PreV26 />;
  const { integration: i, uiBuild: u } = data;
  const pages = u.lightningPagesByType;
  return (
    <div className="space-y-6">
      <CollapsibleSection title="Connected app use" meta={data.connectedAppUse ? `${data.connectedAppUse.length} apps` : NOT_CAPTURED}>
        {data.connectedAppUse ? (
          <Table
            head={['App', 'Last used', 'Users', 'Uses']}
            rows={data.connectedAppUse.map((c) => [
              c.appName,
              c.lastUsedDate ? new Date(c.lastUsedDate).toLocaleDateString() : 'Never',
              c.userCount,
              c.useCount,
            ])}
          />
        ) : (
          <Missing />
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Package flags" meta={data.packageFlags ? `${data.packageFlags.length} packages` : NOT_CAPTURED}>
        {data.packageFlags ? (
          <Table
            head={['Package', 'Version', 'Managed', 'Deprecated', 'Security reviewed', 'Password protected', 'Release state', 'Out of date']}
            rows={data.packageFlags.map((p) => [
              p.namespacePrefix ? `${p.name} (${p.namespacePrefix})` : p.name,
              p.version ?? 'Unknown',
              yn(p.isManaged),
              yn(p.isDeprecated),
              yn(p.isSecurityReviewed),
              yn(p.isPasswordProtected),
              p.releaseState ?? 'Unknown',
              'Unknown',
            ])}
          />
        ) : (
          <Missing />
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Integration" defaultOpen={false}>
        <div className="space-y-4">
          <Table
            head={['Integration surface', 'Count']}
            rows={[
              ['Named credentials', num(i.namedCredentials?.length)],
              ['Remote site settings', num(i.remoteSites?.length)],
              ['Outbound messages', num(i.outboundMessages?.length)],
              ['Platform events', num(i.platformEvents?.length)],
              [
                'API calls used today',
                apiLimit
                  ? `${(apiLimit.Max - apiLimit.Remaining).toLocaleString()} of ${apiLimit.Max.toLocaleString()}`
                  : NOT_CAPTURED,
              ],
            ]}
          />
          {i.namedCredentials && i.namedCredentials.length > 0 && (
            <Table head={['Named credential', 'Endpoint host']} rows={i.namedCredentials.map((n) => [n.name, n.host ?? 'Unknown'])} />
          )}
          {i.remoteSites && i.remoteSites.length > 0 && (
            <Table
              head={['Remote site', 'Host', 'Active']}
              rows={i.remoteSites.map((r) => [r.name, r.host ?? 'Unknown', r.active ? 'Yes' : 'No'])}
            />
          )}
          {i.outboundMessages && i.outboundMessages.length > 0 && (
            <Table head={['Outbound message', 'Object']} rows={i.outboundMessages.map((o) => [o.name, o.object ?? 'Unknown'])} />
          )}
          {i.platformEvents && i.platformEvents.length > 0 && (
            <Table head={['Platform event']} rows={i.platformEvents.map((e) => [e])} />
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="UI build" defaultOpen={false}>
        <div className="space-y-4">
          <Table
            head={['Component', 'Count']}
            rows={[
              ['Lightning web components', num(u.lwc)],
              ['Aura components', num(u.aura)],
              ['Visualforce pages', num(u.visualforcePages)],
              ['Visualforce components', num(u.visualforceComponents)],
              ['Experience sites', num(u.experienceSites?.length)],
              ...(pages
                ? Object.entries(pages)
                    .sort((a, b) => b[1] - a[1])
                    .map(([t, n]): [string, string] => [`Lightning pages: ${t}`, n.toLocaleString()])
                : [['Lightning pages', NOT_CAPTURED] as [string, string]]),
            ]}
          />
          {u.experienceSites && u.experienceSites.length > 0 && (
            <Table head={['Experience site', 'Status']} rows={u.experienceSites.map((s) => [s.name, s.status ?? 'Unknown'])} />
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
